import type { RollLog, RollTerm } from './combat-types.js';

const signed = (value: number) => `${value < 0 ? '−' : '+'}${Math.abs(value)}`;
const die = (roll: RollLog) => `${roll.dice.length > 1 && roll.kind === 'heal' ? roll.dice.length : ''}d${roll.sides}`;
const term = (t: RollTerm) => `${signed(t.value)} ${t.label}`;

/** What the roll deals or decides: "6 damage", "success", "4 HP". */
export function rollOutcome(roll: RollLog): string {
  switch (roll.outcome) {
    case 'miss': return 'Miss';
    case 'crit': return `Critical hit, ${roll.result} damage`;
    case 'hit': return `Hit, ${roll.result} damage`;
    case 'success': return 'Success';
    case 'fail': return 'Failure';
    default: return roll.kind === 'heal' ? `${roll.result} HP` : `${roll.result}`;
  }
}

/** The roll on one line, for text logs: `d8 [6] +3 STR = 9, −2 Armor → 7`. */
export function summarizeRoll(roll: RollLog): string {
  const parts = [`${die(roll)} [${roll.dice.join(', ')}]`];
  if (roll.dropped.length) parts.push(`dropped [${roll.dropped.join(', ')}]`);
  if (roll.explosions.length) parts.push(`exploded [${roll.explosions.join(', ')}]`);
  if (roll.outcome === 'miss') return `${parts.join(' ')} miss`;
  parts.push(...roll.terms.map(term));
  if (roll.terms.length || roll.explosions.length || roll.dice.length > 1 || roll.dropped.length) parts.push(`= ${roll.total}`);
  if (roll.dc !== undefined) parts.push(`vs DC ${roll.dc}: ${roll.outcome === 'success' ? 'success' : 'failure'}`);
  if (roll.reductions.length) parts.push(`, ${roll.reductions.map(term).join(' ')} → ${roll.result}`);
  else if (roll.kind === 'heal' && roll.result !== roll.total) parts.push(`→ ${roll.result} HP`);
  return parts.join(' ').replace(' ,', ',');
}

/** The roll in words, one line per fact, for the log's hover: the dice, what moved them and what came off the total. */
export function describeRoll(roll: RollLog): string[] {
  const lines: string[] = [];
  const subject = roll.name ? `${roll.name} (${die(roll)})` : die(roll);
  lines.push(roll.dc !== undefined ? `${subject} against DC ${roll.dc}: ${rollOutcome(roll).toLowerCase()}` : `${subject}: ${rollOutcome(roll)}`);
  const [primary, ...rest] = roll.dice;
  if (roll.kind === 'attack') {
    lines.push(`Primary Die ${primary}${roll.primaryFrom !== undefined && roll.primaryFrom !== primary ? ` (rolled ${roll.primaryFrom})` : ''}${rest.length ? `, other dice ${rest.join(', ')}` : ''}.`);
  } else lines.push(`Rolled ${roll.dice.join(', ')}.`);
  if (roll.dropped.length) lines.push(`Dropped ${roll.dropped.join(', ')}.`);
  if (roll.explosions.length) lines.push(`The Primary Die rolled its highest face and exploded: ${roll.explosions.join(', then ')}${roll.explosions.length > 1 ? ', each adding to the damage' : ', added to the damage'}.`);
  for (const tag of roll.tags) lines.push(tag.detail ? `${tag.label}: ${tag.detail}` : tag.label);
  if (roll.outcome !== 'miss') {
    if (roll.terms.length) lines.push(`Added: ${roll.terms.map(t => `${signed(t.value)} ${t.label}`).join(', ')}. Total ${roll.total}.`);
    if (roll.reductions.length) lines.push(`Taken off: ${roll.reductions.map(t => `${signed(t.value)} ${t.label}`).join(', ')}. Result ${roll.result}.`);
  }
  return lines;
}
